import React, { useEffect, useMemo } from 'react';
import DatePickerReact from 'react-datepicker';
import { useAppContext } from '../../../../Services/AppContext';
import { getMomentDate } from '../../../../Utils/dates.util';
import { Moment } from 'moment';
import useHolidays from '../../Hooks/useHolidays';

interface Props {
	availabilities?: { date: string; percentageFree: number }[];
	onDateSelect: (date: Moment) => void;
	onDateRangeChange: (dateFrom: Moment, dateTo: Moment) => void;
}

const CalendarPicker: React.FC<Props> = ({
	availabilities,
	onDateSelect,
	onDateRangeChange,
}) => {
	const { language } = useAppContext();
	const monthsShown = 2;
	const { holidays, checkIfHaveHolidaysForYear } = useHolidays();

	const handleSelectDate = (value: Date | null) => {
		const date = getMomentDate(value);
		onDateSelect(date);
	};

	const computeVisibleRange = (date: Date) => {
		const dateFrom = new Date(date.getFullYear(), date.getMonth(), 1);
		const dateTo = new Date(
			date.getFullYear(),
			date.getMonth() + monthsShown,
			0,
		);

		//IF USER GOES OUT OF YEAR BOUNDARY, WE NEED TO FETCH HOLIDAYS FOR NEXT OR PREVIOUS YEAR
		checkIfHaveHolidaysForYear(dateFrom.getFullYear());
		checkIfHaveHolidaysForYear(dateTo.getFullYear());

		onDateRangeChange(getMomentDate(dateFrom), getMomentDate(dateTo));
	};

	const handleOnMonthChange = (date: Date) => {
		computeVisibleRange(date);
	};

	useEffect(() => {
		computeVisibleRange(new Date());
	}, []);

	const highlightDates = useMemo(() => {
		const highlights: (Date | Record<string, Date[]>)[] = [];

		const getClassName = (percentage: number): string => {
			if (percentage === 100) return 'emptyDay';
			if (percentage >= 75) return 'almostEmptyDay';
			if (percentage >= 25) return 'halfFullDay';
			if (percentage > 0) return 'almostFullDay';
			return 'fullDay';
		};

		const highlightGroups: Record<string, Date[]> = {};

		availabilities?.forEach(item => {
			const date = new Date(item.date);
			const className = getClassName(item.percentageFree);
			const fullClass = `react-datepicker__day--highlighted-${className}`;

			if (!highlightGroups[fullClass]) {
				highlightGroups[fullClass] = [];
			}
			highlightGroups[fullClass].push(date);
		});

		if (holidays?.length) {
			highlightGroups['react-datepicker__day--highlighted-holiday'] = holidays;
		}

		for (const [className, dates] of Object.entries(highlightGroups)) {
			if (dates.length > 0) {
				highlights.push({ [className]: dates });
			}
		}

		return highlights;
	}, [availabilities, holidays]);

	return (
		<div className='calendarPicker'>
			<DatePickerReact
				inline
				locale={language}
				monthsShown={monthsShown}
				highlightDates={highlightDates}
				onChange={handleSelectDate}
				onMonthChange={handleOnMonthChange}
			/>
		</div>
	);
};

export default CalendarPicker;
